import {Component,Input} from "@angular/core";
import {ControlContainer,NgForm} from "@angular/forms";
import {RegleAttributionDetail,TypeModeGestion} from "@components/admin/frais/indemnites/indemnites-regles-attribution/detail/indemnites-regle-attribution-detail.component";

/**
 * Onglet généralités du détail règle d'attribution
 */
@Component({
    host: {'data-test-id': 'indemnites-regle-attribution-generalites'},
    selector: 'indemnites-regle-attribution-generalites',
    templateUrl: './indemnites-regle-attribution-generalites.component.html',
    viewProviders: [{provide: ControlContainer,useExisting: NgForm}]
})
export class IndemnitesRegleAttributionGeneralitesComponent {
    /** Règle d'attribution */
    @Input() regleAttribution: RegleAttributionDetail;

    /** Référence vers l'enum pour le template */
    TypeModeGestion = TypeModeGestion;

    /** Référence vers l'enum des types de mission pour le template */
    TypeMissionCalendaire = TypeMissionCalendaire;

    /** Indique qu'une saisie horaire a été commencée */
    horaireModifie: boolean = false;

    /** Heures partielles saisies avant les minutes */
    heuresPartielles: {[champ: string]: string} = {};

    /**
     * Gestion du changement du mode de gestion
     */
    onModeGestionChange(): void {
        //Réinitialisation des durées hors du mode temps passé
        if (this.regleAttribution.modeGestion !== TypeModeGestion.TEMPS_PASSE) {
            this.regleAttribution.dureeMin = 0;
            this.regleAttribution.dureeMax = 24;
        }

        //Réinitialisation des horaires hors du mode tranche horaire
        if (this.regleAttribution.modeGestion !== TypeModeGestion.TRANCHE_HORAIRE) {
            this.regleAttribution.heureDeb = null;
            this.regleAttribution.heureFin = null;
        }
    }

    /**
     * Mémorise les chiffres saisis dans la partie heure
     */
    onHoraireKeydown(event: KeyboardEvent, champ: 'heureDeb' | 'heureFin'): void {
        //Le champ time ne transmet pas au modèle une valeur sans minutes
        if (/^\d$/.test(event.key) && !this.regleAttribution[champ] && (this.heuresPartielles[champ]?.length || 0) < 2) {
            this.heuresPartielles[champ] = (this.heuresPartielles[champ] || '') + event.key;
        }
    }

    /**
     * Complète les minutes absentes avec zéro
     */
    completerMinutes(champ: 'heureDeb' | 'heureFin'): void {
        const heurePartielle: string = this.heuresPartielles[champ];
        const heure: number = Number(heurePartielle);

        //Reproduit le comportement V9 uniquement pour une heure valide sans minutes
        if (!this.regleAttribution[champ] && /^\d{1,2}$/.test(heurePartielle || '') && heure >= 0 && heure <= 23) {
            this.regleAttribution[champ] = heurePartielle.padStart(2, '0') + ':00';
        }

        delete this.heuresPartielles[champ];
    }

    /**
     * Signale une modification des champs horaires
     */
    onHoraireChange(): void {
        this.horaireModifie = true;
    }

    /**
     * Gestion du changement des cases calendaires
     */
    onChangeCalendaire(propriete: 'aucunExcedantCalendaire' | 'excedantCalendaire',isChecked: boolean): void {
        this.regleAttribution[propriete] = isChecked;

        //Coche la case toutes missions si les 2 autres cases sont cochées
        if (this.regleAttribution.excedantCalendaire && this.regleAttribution.aucunExcedantCalendaire) {
            this.regleAttribution.toutesMissions = true;
        }

        //Décoche la case toutes missions si une des 2 autres cases est décochée
        if (!this.regleAttribution.excedantCalendaire || !this.regleAttribution.aucunExcedantCalendaire) {
            this.regleAttribution.toutesMissions = false;
        }

        //Coche premier jour si uniquement mission n'excédant pas une journée calendaire
        if (this.regleAttribution.aucunExcedantCalendaire && !this.regleAttribution.excedantCalendaire) {
            this.regleAttribution.applicablePremJour = true;
            this.regleAttribution.applicableJourInterm = false;
            this.regleAttribution.applicableDernJour = false;
        }
    }

    /**
     * Gestion du changement de la case toutes missions
     */
    onChangeToutesMissions(isChecked: boolean): void {
        this.regleAttribution.toutesMissions = isChecked;
        this.regleAttribution.excedantCalendaire = isChecked;
        this.regleAttribution.aucunExcedantCalendaire = isChecked;
    }

    /**
     * Indique si la case "Premier jour de la mission" doit être verrouillée
     */
    isApplicablePremJourDisabled(): boolean {
        return this.typeMissionCalendaire === TypeMissionCalendaire.MISSION_N_EXCEDANT_PAS_UN_JOUR;
    }

    /**
     * Indique si au moins un type de mission est sélectionné
     */
    isApplicableValid(): boolean {
        return !!(this.regleAttribution?.excedantCalendaire || this.regleAttribution?.aucunExcedantCalendaire);
    }

    /**
     * Indique si les champs spécifiques au mode de gestion sont valides
     */
    isSpecialInputsValid(): boolean {
        if (this.regleAttribution?.modeGestion === TypeModeGestion.TRANCHE_HORAIRE) {
            return !!this.regleAttribution.heureDeb && !!this.regleAttribution.heureFin && !this.isHeureFinWarning();
        }

        if (this.regleAttribution?.modeGestion === TypeModeGestion.TEMPS_PASSE) {
            return !this.isDureeMinWarning() && !this.isDureeMaxRangeWarning() && !this.isDureeMaxWarning();
        }

        return true;
    }

    /**
     * Retourne la clé i18n du tooltip du mode de gestion
     */
    getModeGestionTooltip(): string {
        if (this.regleAttribution?.modeGestion === TypeModeGestion.UNE_FOIS_PAR_JOUR) {
            return 'admin.frais.indemnites.reglesAttribution.detail.generalites.modeGestionTooltipUneFoisParJour';
        }

        if (this.regleAttribution?.modeGestion === TypeModeGestion.TEMPS_PASSE) {
            return 'admin.frais.indemnites.reglesAttribution.detail.generalites.modeGestionTooltipTempsPasse';
        }

        if (this.regleAttribution?.modeGestion === TypeModeGestion.TRANCHE_HORAIRE) {
            return 'admin.frais.indemnites.reglesAttribution.detail.generalites.modeGestionTooltipTrancheHoraire';
        }

        return 'admin.frais.indemnites.reglesAttribution.detail.generalites.modeGestionTooltipDefault';
    }

    /**
     * Retourne la clé i18n du tooltip pour durée min
     */
    getDureeMinTooltipKey(): string {
        //Avertissement si la durée min est hors plage autorisée
        if (this.isDureeHorsPlage(this.regleAttribution?.dureeMin)) {
            return 'admin.frais.indemnites.reglesAttribution.detail.generalites.dureeMinTooltipWarningRange';
        }

        //Message d'information V9 par défaut
        return 'admin.frais.indemnites.reglesAttribution.detail.generalites.dureeMinTooltipInfo';
    }

    /**
     * Indique si l'icône avertissement doit être affichée sur durée min
     */
    isDureeMinWarning(): boolean {
        return this.isDureeHorsPlage(this.regleAttribution?.dureeMin);
    }

    /**
     * Indique si la durée max est hors de la plage autorisée
     */
    isDureeMaxRangeWarning(): boolean {
        return this.isDureeHorsPlage(this.regleAttribution?.dureeMax);
    }

    /**
     * Indique si l'avertissement doit être affiché sur durée max
     */
    isDureeMaxWarning(): boolean {
        //Avertissement uniquement en mode temps passé
        if (this.regleAttribution?.modeGestion !== TypeModeGestion.TEMPS_PASSE) {
            return false;
        }

        //Comme en V9, la comparaison ne se fait que lorsque les deux durées sont renseignées
        if (!this.regleAttribution?.dureeMin || !this.regleAttribution?.dureeMax) {
            return false;
        }

        //La durée max doit être strictement supérieure à la durée min
        return this.toSafeNumber(this.regleAttribution.dureeMin) >= this.toSafeNumber(this.regleAttribution.dureeMax);
    }

    /**
     * Indique si l'avertissement de tranche horaire doit être affiché sur heure de fin
     */
    isHeureFinWarning(): boolean {
        //Contrôle uniquement en mode tranche horaire
        if (this.regleAttribution?.modeGestion !== TypeModeGestion.TRANCHE_HORAIRE) {
            return false;
        }

        //La vérification V9 ne se fait que si les deux champs sont saisis
        if (!this.regleAttribution?.heureDeb || !this.regleAttribution?.heureFin) {
            return this.horaireModifie;
        }

        //Règle V9: heureDeb doit être strictement antérieure à heureFin
        return this.regleAttribution.heureDeb >= this.regleAttribution.heureFin;
    }

    /**
     * Vérifie si une durée est hors plage [0,24]
     */
    private isDureeHorsPlage(duree: number): boolean {
        //Contrôle uniquement en mode temps passé
        if (this.regleAttribution?.modeGestion !== TypeModeGestion.TEMPS_PASSE) {
            return false;
        }

        const valeur = this.toSafeNumber(duree);
        return valeur < 0 || valeur > 24;
    }

    /**
     * Sécurise la conversion numérique
     */
    private toSafeNumber(valeur: number): number {
        return Number.isFinite(Number(valeur)) ? Number(valeur) : 0;
    }

    /**
     * Type de mission affiché dans la liste selon les booléens existants
     */
    get typeMissionCalendaire(): TypeMissionCalendaire {
        if (this.regleAttribution?.aucunExcedantCalendaire && this.regleAttribution?.excedantCalendaire) {
            return TypeMissionCalendaire.TOUTES_MISSIONS;
        }

        if (this.regleAttribution?.excedantCalendaire) {
            return TypeMissionCalendaire.MISSION_EXCEDANT_UN_JOUR;
        }

        if (this.regleAttribution?.aucunExcedantCalendaire) {
            return TypeMissionCalendaire.MISSION_N_EXCEDANT_PAS_UN_JOUR;
        }

        return TypeMissionCalendaire.AUCUN;
    }

    /**
     * Met à jour les booléens existants depuis le type de mission sélectionné
     */
    set typeMissionCalendaire(typeMission: TypeMissionCalendaire) {
        if (!this.regleAttribution || typeMission == null) {
            return;
        }

        //Remise à zéro explicite quand aucun type de mission n'est sélectionné
        if (typeMission === TypeMissionCalendaire.AUCUN) {
            this.regleAttribution.toutesMissions = false;
            this.regleAttribution.aucunExcedantCalendaire = false;
            this.regleAttribution.excedantCalendaire = false;
            this.regleAttribution.applicablePremJour = false;
            this.regleAttribution.applicableJourInterm = false;
            this.regleAttribution.applicableDernJour = false;
            return;
        }

        this.regleAttribution.toutesMissions = typeMission === TypeMissionCalendaire.TOUTES_MISSIONS;
        this.regleAttribution.aucunExcedantCalendaire = typeMission !== TypeMissionCalendaire.MISSION_EXCEDANT_UN_JOUR;
        this.regleAttribution.excedantCalendaire = typeMission !== TypeMissionCalendaire.MISSION_N_EXCEDANT_PAS_UN_JOUR;

        //Conservation du comportement existant pour les missions n'excédant pas une journée
        if (typeMission === TypeMissionCalendaire.MISSION_N_EXCEDANT_PAS_UN_JOUR) {
            this.regleAttribution.applicablePremJour = true;
            this.regleAttribution.applicableJourInterm = false;
            this.regleAttribution.applicableDernJour = false;
        }
    }
}

/** Types de mission disponibles pour l'application de la règle */
export enum TypeMissionCalendaire {
    AUCUN = 'AUCUN',
    TOUTES_MISSIONS = 'TOUTES_MISSIONS',
    MISSION_N_EXCEDANT_PAS_UN_JOUR = 'MISSION_N_EXCEDANT_PAS_UN_JOUR',
    MISSION_EXCEDANT_UN_JOUR = 'MISSION_EXCEDANT_UN_JOUR'
}
